/* eslint-disable react/jsx-no-target-blank */
import React, {FC} from 'react'
import {useIntl} from 'react-intl'
import clsx from 'clsx'
import {KTSVG} from '../../../../helpers'
import {SidebarMenuItemWithSub} from './SidebarMenuItemWithSub'
import {SidebarMenuItem} from './SidebarMenuItem'
import {useAuth} from '../../../../../app/modules/auth'
import {useSpeechToSpeech} from '../../../../../app/context/SpeechToSpeechContext'
import {useLayout} from '../../../core'
import {useNavigationMenu} from '../../../../../app/UI/roles-permissions/core/useNavigationMenu'
import {FeatureNode, RenderType} from '../../../../../app/UI/roles-permissions/core/_models'

const GUIDELINE_ICON_COLOR = 'rgb(163, 162, 66)'
const GUIDELINE_ICON_BOX = {width: '20px', height: '20px'} as const

const ChromeGuidelineIcon = () => (
  <span
    className='me-3 fs-6 flex-shrink-0 d-flex align-items-center justify-content-center'
    style={GUIDELINE_ICON_BOX}
  >
    <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 48 48'>
      <path
        fill='#4caf50'
        d='M44,24c0,11.044-8.956,20-20,20S4,35.044,4,24S12.956,4,24,4S44,12.956,44,24z'
      />
      <path
        fill='#ffc107'
        d='M24,4v20l8,4l-8.843,16c0.317,0,0.526,0,0.843,0c11.053,0,20-8.947,20-20S35.053,4,24,4z'
      />
      <path
        fill='#f44336'
        d='M41.84,15H24v13l-3-1L7.16,13.26H7.14C10.68,7.69,16.91,4,24,4C31.8,4,38.55,8.48,41.84,15z'
      />
      <path fill='#dd2c00' d='M7.158,13.264l8.843,14.862L21,27L7.158,13.264z' />
      <path fill='#558b2f' d='M23.157,44l8.934-16.059L28,25L23.157,44z' />
      <path fill='#f9a825' d='M41.865,15H24l-1.579,4.58L41.865,15z' />
      <path fill='#fff' d='M33,24c0,4.969-4.031,9-9,9s-9-4.031-9-9s4.031-9,9-9S33,19.031,33,24z' />
      <path fill='#2196f3' d='M31,24c0,3.867-3.133,7-7,7s-7-3.133-7-7s3.133-7,7-7S31,20.133,31,24z' />
    </svg>
  </span>
)

interface TroubleshootingGuideline {
  id: string
  text: string
  icon?: string
  customIcon?: React.ReactNode
}

const INTERPRETER_TROUBLESHOOTING_GUIDELINES: TroubleshootingGuideline[] = [
  {
    id: 'chrome',
    text: 'Login From Google Chrome',
    customIcon: <ChromeGuidelineIcon />,
  },
  {
    id: 'notifications',
    text: 'Enable Browser Notifications',
    icon: 'bi-bell',
  },
  {
    id: 'cookies',
    text: 'Allow Third-Party Cookies',
    icon: 'bi-shield-check',
  },
  {
    id: 'microphone',
    text: 'Allow Microphone Access',
    icon: 'bi-mic',
  },
  // {
  //   id: 'headset',
  //   text: 'Use a headset and close other calling apps (Teams, Zoom)',
  //   icon: 'bi-headphones',
  // },
  {
    id: 'vpn',
    text: 'Turn off VPN; restart your device if problems continue',
    icon: 'bi-shield-slash',
  },
  // {
  //   id: 'wired-network',
  //   text: 'Use a wired network connection instead of Wi-Fi when possible',
  //   icon: 'bi-ethernet',
  // },
  {
    id: 'single-session',
    text: 'Only use logged-in CCP widget session in a single browser',
    icon: 'bi-window',
  },
  {
    id: 'tab-focus',
    text: 'Keep this browser tab open and in focus during calls',
    icon: 'bi-pin-angle',
  },
  {
    id: 'sign-out',
    text: 'Sign out and Sign back in',
    icon: 'bi-box-arrow-right',
  },
  {
    id: 'connection-test',
    text: 'Run Connection Test from the CCP Widget Settings',
    icon: 'bi-wifi',
  },
]

function TroubleshootingGuidelineRow({guideline}: {guideline: TroubleshootingGuideline}) {
  return (
    <div className='d-flex align-items-start mb-3'>
      {guideline.customIcon ?? (
        <i
          className={clsx(
            'bi',
            guideline.icon,
            'me-3 fs-6 flex-shrink-0 d-flex align-items-center justify-content-center',
          )}
          style={{color: GUIDELINE_ICON_COLOR, ...GUIDELINE_ICON_BOX}}
        />
      )}
      <span className='text-dark fs-6' style={{lineHeight: '1.5'}}>
        {guideline.text}
      </span>
    </div>
  )
}

const TroubleshootingSidebarItem = () => {
  return (
    <div className='menu-item mt-3 mb-5'>
      <div className='menu-content px-3 py-3 bg-light rounded'>
        <div className='d-flex align-items-start mb-4'>
          <i
            className='bi bi-exclamation-circle text-danger me-2 fs-5 flex-shrink-0'
            style={{lineHeight: '1.5'}}
          ></i>
          <div className='d-flex flex-column'>
            <span className='text-dark fw-bold fs-6'>Troubleshooting</span>
            <span className='text-gray-500 fs-7 mt-1'>GUIDELINES</span>
          </div>
        </div>
        <div className='d-flex flex-column mb-3'>
          {INTERPRETER_TROUBLESHOOTING_GUIDELINES.map((guideline) => (
            <TroubleshootingGuidelineRow key={guideline.id} guideline={guideline} />
          ))}
        </div>
        <div className='separator separator-dashed my-3'></div>
        <div className='d-flex align-items-center mb-3'>
          <i
            className='bi bi-question-circle me-3 fs-6 flex-shrink-0 d-flex align-items-center justify-content-center text-dark'
            style={{width: '20px', height: '20px'}}
          ></i>
          <span className='text-dark fs-6' style={{lineHeight: '1.5'}}>
            Still not getting calls? Reach out to us
          </span>
        </div>
        <div className='d-flex align-items-center'>
          <i
            className='bi bi-headset me-3 fs-6 flex-shrink-0 d-flex align-items-center justify-content-center'
            style={{color: GUIDELINE_ICON_COLOR, width: '20px', height: '20px'}}
          ></i>
          <a
            href='https://connectsupport.helpwise.help/'
            target='_blank'
            className='text-decoration-none fs-6'
            style={{color: GUIDELINE_ICON_COLOR, lineHeight: '1.5'}}
          >
            Help Support
          </a>
        </div>
      </div>
    </div>
  )
}

const SpeechToSpeechSidebarItem = () => {
  const {toggleWidget} = useSpeechToSpeech()
  const {config} = useLayout()
  const {app} = config
  const iconType = app?.sidebar?.default?.menu?.iconType ?? 'font'

  return (
    <div className='menu-item'>
      <button
        type='button'
        onClick={toggleWidget}
        className={clsx('menu-link without-sub')}
        style={{
          background: 'none',
          border: 'none',
          width: '100%',
          textAlign: 'left',
          cursor: 'pointer',
          // padding: 0,
          font: 'inherit',
          color: 'inherit',
        }}
      >
        {iconType === 'svg' && (
          <span className='menu-icon'>
            <KTSVG path='/media/icons/duotune/communication/com013.svg' className='svg-icon-2' />
          </span>
        )}
        {iconType === 'font' && <i className='bi bi-mic fs-3 menu-icon' />}
        <span className='menu-title'>Speech to Speech AI</span>
      </button>
    </div>
  )
}

const DynamicSidebarMenu: FC<{nodes: FeatureNode[]}> = ({nodes}) => {
  const {currentUser} = useAuth()
  const userType = currentUser?.result.userType
  const showTranscriptions = process.env.REACT_APP_SERVER !== 'PROD'
  const canAccessAIInterpretation =
    currentUser?.result.canAccessAIInterpretation === true || userType === 'SYSTEM'
  const hasTranslations = currentUser?.result.translations === true

  const isVisible = (code: string): boolean => {
    switch (code) {
      case 'transcriptions':
        return showTranscriptions
      case 'translations':
        return userType === 'SYSTEM' ? true : hasTranslations
      case 'speechtospeechwidget':
        return canAccessAIInterpretation
      case 'connection_test':
      case 'troubleshooting':
        return userType === 'INTERPRETER'
      default:
        return true
    }
  }

  const resolvePath = (node: FeatureNode): string => {
    switch (node.code) {
      case 'profile':
        return '/profile/' + currentUser?.result.code
      case 'company_profile':
        return userType === 'SYSTEM'
          ? '/company-profile/' + currentUser?.result.fK_Customer
          : '/customers/' + currentUser?.result.fK_Customer
      case 'billing':
        return userType === 'SYSTEM' ? '/billing/2' : '/billing/1'
      case 'reporting':
      case 'ai-reporting':
      case 'ai_reporting':
        return node.path || '/ai-reporting'
      default:
        return node.path || '#'
    }
  }

  const renderNode = (node: FeatureNode): JSX.Element | null => {
    if (node.renderType === RenderType.Section) {
      return (
        <div className='menu-item' key={node.code}>
          <div className='menu-content mb-3 ms-1'>
            <span className='menu-section text-uppercase fs-5 fw-bold' style={{color: '#D7383C'}}>
              {node.name}
            </span>
          </div>
        </div>
      )
    }

    if (node.renderType === RenderType.Widget) {
      if (node.code === 'speechtospeechwidget') return <SpeechToSpeechSidebarItem key={node.code} />
      if (node.code === 'troubleshooting') return <TroubleshootingSidebarItem key={node.code} />
      return null
    }

    if (node.renderType === RenderType.Submenu && node.children && node.children.length > 0) {
      return (
        <SidebarMenuItemWithSub
          key={node.code}
          to={node.path || '#'}
          title={node.name}
          icon={node.icon || undefined}
          fontIcon={node.fontIcon || undefined}
          hasBullet={node.hasBullet}
        >
          {renderNodes(node.children)}
        </SidebarMenuItemWithSub>
      )
    }

    return (
      <SidebarMenuItem
        key={node.code}
        to={resolvePath(node)}
        title={node.name}
        icon={node.icon || (node.code === 'reporting' ? '/media/icons/duotune/general/gen024.svg' : undefined)}
        fontIcon={node.fontIcon || (node.code === 'reporting' ? 'bi-file-earmark-text' : undefined)}
        hasBullet={node.hasBullet}
      />
    )
  }

  const renderNodes = (items: FeatureNode[]) =>
    items.filter((n) => isVisible(n.code)).map((node) => renderNode(node))

  return <>{renderNodes(nodes)}</>
}

const SidebarMenuMain = () => {
  const intl = useIntl()
  const {currentUser, logout} = useAuth()
  const {navigation} = useNavigationMenu(!!currentUser)
  // show transcriptions only on non-production environments (DEV and QA/staging)
  const showTranscriptions = process.env.REACT_APP_SERVER !== 'PROD'
  // AI Terp pilot: per-user allow-list (BE: CanAccessAIInterpretation on /accounts/loggeduser)
  const canAccessAIInterpretation =
    currentUser?.result.canAccessAIInterpretation === true ||
    currentUser?.result.userType === 'SYSTEM'

  if (navigation && navigation.length > 0) {
    return <DynamicSidebarMenu nodes={navigation} />
  }

  if (currentUser?.result.userType == 'SYSTEM') {
    return (
      <>
        <SidebarMenuItem
          to='/dashboard'
          icon='/media/icons/duotune/layouts/lay001.svg'
          title={intl.formatMessage({id: 'MENU.DASHBOARD'})}
          fontIcon='bi-app-indicator'
        />
        <SidebarMenuItem
          to='/appointment/1'
          icon='/media/icons/duotune/files/fil002.svg'
          title='Appointment'
          fontIcon='bi-layers'
        />
        {showTranscriptions && (
          <SidebarMenuItem
            to='/transcriptions'
            icon='/media/icons/duotune/files/fil003.svg'
            title='Transcriptions'
            fontIcon='bi-file-text'
          />
        )}

        <SidebarMenuItem
          to='/billing/2'
          icon='/media/icons/duotune/finance/fin002.svg'
          title='Billing'
          fontIcon='bi-layers'
        />
        <SidebarMenuItem
          to='/logs-management'
          icon='/media/interpreter-dashboard/IMG_5.svg'
          title='Call logs'
          fontIcon='bi-journal-text'
        />
        <SidebarMenuItemWithSub
          to='/insight'
          icon='/media/icons/duotune/abstract/abs030.svg'
          title='Insights'
          fontIcon='bi-insight'
        >
          <SidebarMenuItemWithSub to='/logs' title='Logs' fontIcon='bi-logs' hasBullet={true}>
            <SidebarMenuItem to='/logdata/all' title='Login Details' hasBullet={true} />
            <SidebarMenuItem to='/precalldata/all' title='Pre Call Details' hasBullet={true} />
            <SidebarMenuItem
              to='/serviceTypeChange/all'
              title='Service Type Details'
              hasBullet={true}
            />
            <SidebarMenuItem
              to='/premissionsChnage/all'
              title='Permissions Details'
              hasBullet={true}
            />
          </SidebarMenuItemWithSub>
        </SidebarMenuItemWithSub>

        <SidebarMenuItem
          to={'/profile/' + currentUser.result.code}
          icon='/media/icons/duotune/communication/com005.svg'
          title='Profile'
          fontIcon='bi-layers'
        />
        <div className='separator my-3 border-1 border-gray-300'></div>
        <div className='menu-item'>
          <div className='menu-content mb-3 ms-1'>
            <span className='menu-section text-uppercase fs-5 fw-bold' style={{color: '#D7383C'}}>
              Administrator
            </span>
          </div>
        </div>
        <SidebarMenuItem
          to={'/customers'}
          icon='/media/icons/duotune/general/gen022.svg'
          title='Customers'
          fontIcon='bi-layers'
        />

        <SidebarMenuItem
          to='/members/1'
          icon='/media/icons/duotune/communication/com006.svg'
          title='Members'
          fontIcon='bi-layers'
        />
        <SidebarMenuItemWithSub
          to='/crafted/pages/profile'
          icon='/media/icons/duotune/coding/cod001.svg'
          title='Configuration'
          fontIcon='bi-layers'
        >
          <SidebarMenuItem
            to='/apps/language/all'
            title='Language Configuration'
            hasBullet={true}
          />
          <SidebarMenuItem to='/apps/service-type/all' title='Service Types' hasBullet={true} />
          <SidebarMenuItem
            to='/apps/document-management/compliance-documents/all'
            title='Document Management'
            hasBullet={true}
          />
          {/*<SidebarMenuItem to='/apps/special-type/all' title='Specialized Types' hasBullet={true} />*/}
          {/*<SidebarMenuItem to='/apps/industry/all' title='Industries' hasBullet={true} />*/}
          <SidebarMenuItem
            to='/apps/communication/all'
            title='Communication Type'
            hasBullet={true}
          />
          <SidebarMenuItem
            to='/apps/cancellation-reasons/all'
            title='Reasons'
            hasBullet={true}
          />
          <SidebarMenuItem to='/apps/gender/all' title='Gender' hasBullet={true} />
          <SidebarMenuItem to='/apps/ivr/all' title='IVR' hasBullet={true} />
          {/* <SidebarMenuItem to='/apps/time-zone/all' title='Time Zones' hasBullet={true} /> */}
          <SidebarMenuItem to='/apps/country-management/all' title='Countries' hasBullet={true} />
          <SidebarMenuItem to='/email/configuration' title='Emails' hasBullet={true} />
          {process.env.REACT_APP_SERVER === 'DEV' && (
            <SidebarMenuItem
              to='/email/configuration-duplicate'
              title='Emails Duplicate'
              hasBullet={true}
            />
          )}
          <SidebarMenuItem to='/billConfig/configuration' title='Billing' hasBullet={true} />
          <SidebarMenuItemWithSub
            to='/backstopRoute'
            title='Backstop Route Details'
            fontIcon='bi-logs'
            hasBullet={true}
          >
            <SidebarMenuItem
              to='/backstopRoute/languages'
              title='Routing Languages'
              hasBullet={true}
            />
            <SidebarMenuItem
              to='/backstopRoute/ServiceTypes'
              title='Routing Service Types'
              hasBullet={true}
            />
          </SidebarMenuItemWithSub>
          <SidebarMenuItem
            to='/apps/SystemVariables/all'
            title='System Variables'
            hasBullet={true}
          />
          <SidebarMenuItem
            to='/apps/ai-interpretation'
            title='AI Interpretation'
            hasBullet={true}
          />
        </SidebarMenuItemWithSub>
        {currentUser?.result.role !== 'Manager' && (
          <SidebarMenuItem
            to={'/company-profile/' + currentUser?.result.fK_Customer}
            icon='/media/icons/duotune/general/gen022.svg'
            title='Company Profile'
            fontIcon='bi-layers'
          />
        )}
        <SidebarMenuItem
          to='/plunet'
          icon='/media/icons/duotune/files/fil003.svg'
          title='Translations'
          fontIcon='bi-file-earmark-text'
        />

        <SidebarMenuItem
          to={'/reports'}
          icon='/media/icons/duotune/general/gen024.svg'
          title='Reports'
          fontIcon='bi-file-earmark-text'
        />
        <SidebarMenuItem
          to='/ai-reporting'
          icon='/media/icons/duotune/general/gen024.svg'
          title='Reporting'
          fontIcon='bi-file-earmark-text'
        />
        {canAccessAIInterpretation && <SpeechToSpeechSidebarItem />}
        {/* <SidebarMenuItem
          to='/quick-start'
          icon='/media/icons/duotune/general/gen002.svg'
          title='Quick Start'
          fontIcon='bi-layers'
        /> */}
      </>
    )
  } else if (currentUser?.result.userType == 'INTERPRETER') {
    return (
      <>
        <SidebarMenuItem
          to='/dashboard'
          icon='/media/icons/duotune/layouts/lay001.svg'
          title={intl.formatMessage({id: 'MENU.DASHBOARD'})}
          fontIcon='bi-app-indicator'
        />
        <SidebarMenuItem
          to='/appointment/1'
          icon='/media/icons/duotune/files/fil002.svg'
          title='Appointment'
          fontIcon='bi-layers'
        />
        {showTranscriptions && (
          <SidebarMenuItem
            to='/transcriptions'
            icon='/media/icons/duotune/files/fil003.svg'
            title='Transcriptions'
            fontIcon='bi-file-text'
          />
        )}
        <SidebarMenuItem
          to='/billing/1'
          icon='/media/icons/duotune/finance/fin002.svg'
          title='Billing'
          fontIcon='bi-layers'
        />
        <SidebarMenuItem
          to='/logs-management'
          icon='/media\interpreter-dashboard\IMG_5.svg'
          title='Call logs'
          fontIcon='bi-journal-text'
        />
        <SidebarMenuItem
          to={'/profile/' + currentUser.result.code}
          icon='/media/icons/duotune/communication/com005.svg'
          title='Profile'
          fontIcon='bi-layers'
        />
        <SidebarMenuItem
          to='/connection-test'
          icon='/media/icons/duotune/electronics/elc004.svg'
          title='Connection Test'
          fontIcon='bi-wifi'
        />
        {canAccessAIInterpretation && <SpeechToSpeechSidebarItem />}
        <TroubleshootingSidebarItem />
      </>
    )
  } else if (currentUser?.result.userType == 'CONSUMER') {
    const hasBilling = currentUser?.result.userFeatures?.some(
      (uf) => uf.feature.code.toLowerCase() === 'billing'
    )
    const hasTranslations = currentUser?.result.translations === true
    const hasReporting = currentUser?.result.userFeatures?.some((uf) => {
      const code = uf.feature?.code?.toLowerCase() ?? ''
      return code === 'reporting' || code === 'ai-reporting' || code === 'ai_reporting' || code.includes('reporting')
    })

    return (
      <>
        <SidebarMenuItem
          to='/dashboard'
          icon='/media/icons/duotune/layouts/lay001.svg'
          title={intl.formatMessage({id: 'MENU.DASHBOARD'})}
          fontIcon='bi-app-indicator'
        />
        <SidebarMenuItem
          to='/appointment/1'
          icon='/media/icons/duotune/files/fil002.svg'
          title='Appointment'
          fontIcon='bi-layers'
        />
        {showTranscriptions && (
          <SidebarMenuItem
            to='/transcriptions'
            icon='/media/icons/duotune/files/fil003.svg'
            title='Transcriptions'
            fontIcon='bi-file-text'
          />
        )}
        {currentUser?.result?.role !== 'User' && hasBilling && (
          <SidebarMenuItem
            to='/billing/1'
            icon='/media/icons/duotune/finance/fin002.svg'
            title='Billing'
            fontIcon='bi-layers'
          />
        )}
        
        {hasTranslations && (
          <SidebarMenuItem
            to='/plunet'
            icon='/media/icons/duotune/files/fil003.svg'
            title='Translations'
            fontIcon='bi-file-earmark-text'
          />
        )}
        
        <SidebarMenuItem
          to='/logs-management'
          icon='/media/interpreter-dashboard/IMG_5.svg'
          title='Call logs'
          fontIcon='bi-journal-text'
        />
        {hasReporting && (
          <SidebarMenuItem
            to='/ai-reporting'
            icon='/media/icons/duotune/general/gen024.svg'
            title='Reporting'
            fontIcon='bi-file-earmark-text'
          />
        )}
        <SidebarMenuItem
          to={'/profile/' + currentUser.result.code}
          icon='/media/icons/duotune/communication/com005.svg'
          title='Profile'
          fontIcon='bi-layers'
        />
        <SidebarMenuItem
          to={'/customers/' + currentUser?.result.fK_Customer}
          icon='/media/icons/duotune/general/gen022.svg'
          title='Company Profile'
          fontIcon='bi-layers'
        />
        {canAccessAIInterpretation && <SpeechToSpeechSidebarItem />}
      </>
    )
  } else {
    return (
      <>
        <SidebarMenuItem
          to='/dashboard'
          icon='/media/icons/duotune/layouts/lay001.svg'
          title={intl.formatMessage({id: 'MENU.DASHBOARD'})}
          fontIcon='bi-app-indicator'
        />
        {canAccessAIInterpretation && <SpeechToSpeechSidebarItem />}
      </>
    )
  }

  return (
    <>
      <SidebarMenuItem
        to='/dashboard'
        icon='/media/icons/duotune/layouts/lay001.svg'
        title={intl.formatMessage({id: 'MENU.DASHBOARD'})}
        fontIcon='bi-app-indicator'
      />
      <SidebarMenuItem
        to='/appointment'
        icon='/media/icons/duotune/files/fil002.svg'
        title='Appointment'
        fontIcon='bi-layers'
      />
      <SidebarMenuItem
        to='/appointment/interpreter'
        icon='/media/icons/duotune/files/fil002.svg'
        title='Interpreter Appointment'
        fontIcon='bi-layers'
      />
      <SidebarMenuItem
        to='/billing'
        icon='/media/icons/duotune/finance/fin002.svg'
        title='Billing'
        fontIcon='bi-layers'
      />
      <SidebarMenuItem
        to='/members/1'
        icon='/media/icons/duotune/communication/com005.svg'
        title='Members'
        fontIcon='bi-layers'
      />

      <SidebarMenuItem
        to='/customers'
        icon='/media/icons/duotune/ecommerce/ecm008.svg'
        title='Customers'
        fontIcon='bi-layers'
      />
      <SidebarMenuItem
        to='/configuration'
        icon='/media/icons/duotune/coding/cod001.svg'
        title='Configuration'
        fontIcon='bi-layers'
      />
      <SidebarMenuItem
        to='/company'
        icon='/media/icons/duotune/general/gen022.svg'
        title='Ad Astra'
        fontIcon='bi-layers'
      />

      {/* <div className='menu-item'>
        <div className='menu-content pt-8 pb-2'>
          <span className='menu-section text-muted text-uppercase fs-8 ls-1'>Crafted</span>
        </div>
      </div>
      <SidebarMenuItemWithSub
        to='/crafted/pages'
        title='Pages'
        fontIcon='bi-archive'
        icon='/media/icons/duotune/general/gen022.svg'
      >
        <SidebarMenuItemWithSub to='/crafted/pages/profile' title='Profile' hasBullet={true}>
          <SidebarMenuItem to='/crafted/pages/profile/overview' title='Overview' hasBullet={true} />
          <SidebarMenuItem to='/crafted/pages/profile/projects' title='Projects' hasBullet={true} />
          <SidebarMenuItem
            to='/crafted/pages/profile/campaigns'
            title='Campaigns'
            hasBullet={true}
          />
          <SidebarMenuItem
            to='/crafted/pages/profile/documents'
            title='Documents'
            hasBullet={true}
          />
          <SidebarMenuItem
            to='/crafted/pages/profile/connections'
            title='Connections'
            hasBullet={true}
          />
        </SidebarMenuItemWithSub>

        <SidebarMenuItemWithSub to='/crafted/pages/wizards' title='Wizards' hasBullet={true}>
          <SidebarMenuItem
            to='/crafted/pages/wizards/horizontal'
            title='Horizontal'
            hasBullet={true}
          />
          <SidebarMenuItem to='/crafted/pages/wizards/vertical' title='Vertical' hasBullet={true} />
        </SidebarMenuItemWithSub>
      </SidebarMenuItemWithSub>
      <SidebarMenuItemWithSub
        to='/crafted/accounts'
        title='Accounts'
        icon='/media/icons/duotune/communication/com006.svg'
        fontIcon='bi-person'
      >
        <SidebarMenuItem to='/crafted/account/overview' title='Overview' hasBullet={true} />
        <SidebarMenuItem to='/crafted/account/settings' title='Settings' hasBullet={true} />
      </SidebarMenuItemWithSub>
      <SidebarMenuItemWithSub
        to='/error'
        title='Errors'
        fontIcon='bi-sticky'
        icon='/media/icons/duotune/general/gen040.svg'
      >
        <SidebarMenuItem to='/error/404' title='Error 404' hasBullet={true} />
        <SidebarMenuItem to='/error/500' title='Error 500' hasBullet={true} />
      </SidebarMenuItemWithSub>
      <SidebarMenuItemWithSub
        to='/crafted/widgets'
        title='Widgets'
        icon='/media/icons/duotune/general/gen025.svg'
        fontIcon='bi-layers'
      >
        <SidebarMenuItem to='/crafted/widgets/lists' title='Lists' hasBullet={true} />
        <SidebarMenuItem to='/crafted/widgets/statistics' title='Statistics' hasBullet={true} />
        <SidebarMenuItem to='/crafted/widgets/charts' title='Charts' hasBullet={true} />
        <SidebarMenuItem to='/crafted/widgets/mixed' title='Mixed' hasBullet={true} />
        <SidebarMenuItem to='/crafted/widgets/tables' title='Tables' hasBullet={true} />
        <SidebarMenuItem to='/crafted/widgets/feeds' title='Feeds' hasBullet={true} />
      </SidebarMenuItemWithSub>
      <div className='menu-item'>
        <div className='menu-content pt-8 pb-2'>
          <span className='menu-section text-muted text-uppercase fs-8 ls-1'>Apps</span>
        </div>
      </div>
      <SidebarMenuItemWithSub
        to='/apps/chat'
        title='Chat'
        fontIcon='bi-chat-left'
        icon='/media/icons/duotune/communication/com012.svg'
      >
        <SidebarMenuItem to='/apps/chat/private-chat' title='Private Chat' hasBullet={true} />
        <SidebarMenuItem to='/apps/chat/group-chat' title='Group Chart' hasBullet={true} />
        <SidebarMenuItem to='/apps/chat/drawer-chat' title='Drawer Chart' hasBullet={true} />
      </SidebarMenuItemWithSub>
      <SidebarMenuItem
        to='/apps/user-management/users'
        icon='/media/icons/duotune/general/gen051.svg'
        title='User management'
        fontIcon='bi-layers'
      />

      <SidebarMenuItem
        to='/apps/user-role-management/roles'
        icon='/media/icons/duotune/general/gen051.svg'
        title='User Role management'
        fontIcon='bi-layers'
      />

      <div className='menu-item'>
        <a
          target='_blank'
          className='menu-link'
          href={process.env.REACT_APP_PREVIEW_DOCS_URL + '/docs/changelog'}
        >
          <span className='menu-icon'>
            <KTSVG path='/media/icons/duotune/general/gen005.svg' className='svg-icon-2' />
          </span>
          <span className='menu-title'>Changelog {process.env.REACT_APP_VERSION}</span>
        </a>
      </div> */}
    </>
  )
}

export {SidebarMenuMain}
